import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  TrendingUp,
  Users,
  Package,
  DollarSign,
  Download,
  FileSpreadsheet,
  FileText,
  AlertTriangle,
  RefreshCw,
  Calendar,
  Filter,
  X,
} from "lucide-react";

export default function AdminReportes() {
  const [vendedores, setVendedores] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filtros para exportación
  const [showFilters, setShowFilters] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [fechaFin, setFechaFin] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [zonaFiltro, setZonaFiltro] = useState("");
  const [zonasDisponibles, setZonasDisponibles] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vendedoresRes, resumenRes, productosRes] = await Promise.all([
        api.get("/reportes/vendedores"),
        api.get("/reportes/resumen"),
        api.get("/productos"),
      ]);
      setVendedores(vendedoresRes.data);
      setResumen(resumenRes.data);
      setProductos(productosRes.data);
      
      // Extract unique zones
      const zonas = [...new Set(vendedoresRes.data.map(v => v.zona).filter(Boolean))];
      setZonasDisponibles(zonas);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  // Productos con stock bajo (< 10 unidades)
  const productosStockBajo = productos.filter((p) => (p.stock_central || 0) < 10);

  const totalVentas = vendedores.reduce((sum, v) => sum + (v.ventas_hoy || 0), 0);
  const totalUnidades = vendedores.reduce((sum, v) => sum + (v.unidades_hoy || 0), 0);
  const promedioMeta =
    vendedores.length > 0
      ? vendedores.reduce((sum, v) => sum + (v.cumplimiento || 0), 0) / vendedores.length
      : 0;

  // Set date range presets
  const setDatePreset = (preset) => {
    const today = new Date();
    let start = new Date();
    
    switch (preset) {
      case 'today':
        start = today;
        break;
      case 'yesterday':
        start.setDate(today.getDate() - 1);
        today.setDate(today.getDate() - 1);
        break;
      case 'week':
        start.setDate(today.getDate() - 7);
        break;
      case 'month':
        start.setDate(1);
        break;
      case 'lastMonth':
        start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const endOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
        setFechaFin(endOfLastMonth.toISOString().split('T')[0]);
        setFechaInicio(start.toISOString().split('T')[0]);
        return;
      default:
        break;
    }
    
    setFechaInicio(start.toISOString().split('T')[0]);
    setFechaFin(today.toISOString().split('T')[0]);
  };

  // Export detailed sales to Excel (CSV)
  const exportDetailedExcel = async () => {
    setExporting(true);
    try {
      let url = `/reportes/ventas-excel?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`;
      if (zonaFiltro) {
        url += `&zona=${encodeURIComponent(zonaFiltro)}`;
      }
      
      const response = await api.get(url, { responseType: 'blob' });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `ventas_${fechaInicio}_a_${fechaFin}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success("Reporte de ventas exportado exitosamente");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Error al exportar el reporte");
    } finally {
      setExporting(false);
    }
  };

  // Exportar a CSV (Excel) - Quick export for today
  const exportToCSV = () => {
    setExporting(true);
    try {
      // Prepare data
      const headers = ["#", "Vendedor", "Zona", "Ventas ($)", "Unidades", "Meta ($)", "Cumplimiento (%)"];
      const rows = vendedores.map((v, i) => [
        i + 1,
        v.nombre,
        v.zona || "-",
        v.ventas_hoy || 0,
        v.unidades_hoy || 0,
        v.meta || 0,
        v.cumplimiento || 0,
      ]);

      // Add totals row
      rows.push(["", "TOTAL", "", totalVentas, totalUnidades, "", `${promedioMeta.toFixed(1)}%`]);

      // Convert to CSV
      const csvContent = [
        headers.join(","),
        ...rows.map((row) => row.join(",")),
      ].join("\n");

      // Create and download file
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fecha = new Date().toLocaleDateString("es-CO").replace(/\//g, "-");
      link.download = `reporte_ventas_${fecha}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Reporte exportado a Excel (CSV)");
    } catch (error) {
      toast.error("Error al exportar");
    } finally {
      setExporting(false);
    }
  };

  // Exportar a PDF (usando printable HTML)
  const exportToPDF = () => {
    setExporting(true);
    try {
      const fecha = new Date().toLocaleDateString("es-CO", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Reporte de Ventas - Baruj Shefa SAS</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #8B4513; margin-bottom: 5px; }
            .date { color: #666; margin-bottom: 20px; }
            .summary { display: flex; gap: 20px; margin-bottom: 30px; flex-wrap: wrap; }
            .summary-card { background: #f5f5f5; padding: 15px; border-radius: 8px; min-width: 150px; }
            .summary-card h3 { margin: 0 0 5px 0; font-size: 12px; color: #666; }
            .summary-card p { margin: 0; font-size: 20px; font-weight: bold; color: #333; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
            th { background: #8B4513; color: white; }
            tr:nth-child(even) { background: #f9f9f9; }
            .text-right { text-align: right; }
            .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>
          <h1>Reporte de Ventas - Baruj Shefa SAS</h1>
          <p class="date">${fecha}</p>
          
          <div class="summary">
            <div class="summary-card">
              <h3>Total Ventas</h3>
              <p>${formatCurrency(totalVentas)}</p>
            </div>
            <div class="summary-card">
              <h3>Unidades Vendidas</h3>
              <p>${totalUnidades}</p>
            </div>
            <div class="summary-card">
              <h3>Vendedores Activos</h3>
              <p>${vendedores.length}</p>
            </div>
            <div class="summary-card">
              <h3>Promedio Meta</h3>
              <p>${promedioMeta.toFixed(1)}%</p>
            </div>
          </div>
          
          <h2>Rendimiento por Vendedor</h2>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Vendedor</th>
                <th>Zona</th>
                <th class="text-right">Ventas</th>
                <th class="text-right">Unidades</th>
                <th class="text-right">Meta</th>
                <th class="text-right">Cumplimiento</th>
              </tr>
            </thead>
            <tbody>
              ${vendedores
                .map(
                  (v, i) => `
                <tr>
                  <td>${i + 1}</td>
                  <td>${v.nombre}</td>
                  <td>${v.zona || "-"}</td>
                  <td class="text-right">${formatCurrency(v.ventas_hoy)}</td>
                  <td class="text-right">${v.unidades_hoy}</td>
                  <td class="text-right">${formatCurrency(v.meta)}</td>
                  <td class="text-right">${v.cumplimiento}%</td>
                </tr>
              `
                )
                .join("")}
            </tbody>
          </table>
          
          <div class="footer">
            <p>Reporte generado automáticamente por el Sistema ERP de Baruj Shefa SAS</p>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open("", "_blank");
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.print();

      toast.success("Reporte listo para imprimir/guardar como PDF");
    } catch (error) {
      toast.error("Error al generar PDF");
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-reportes">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Reportes</h1>
          <p className="text-torta-muted">Resumen del día</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Actualizar
          </Button>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? "border-torta-brown text-torta-brown" : ""}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filtros
          </Button>
          <Button
            variant="outline"
            onClick={exportToCSV}
            disabled={exporting}
            className="border-green-300 text-green-600 hover:bg-green-50"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Resumen
          </Button>
          <Button
            variant="outline"
            onClick={exportToPDF}
            disabled={exporting}
            className="border-red-300 text-red-600 hover:bg-red-50"
          >
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Filtros para exportación detallada */}
      {showFilters && (
        <div className="bg-white rounded-2xl shadow-sm p-6 border border-torta-sand/50">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg text-torta-dark flex items-center gap-2">
              <Download className="w-5 h-5 text-torta-brown" />
              Exportar Ventas Detalladas a Excel
            </h3>
            <button onClick={() => setShowFilters(false)} className="text-torta-muted hover:text-torta-dark">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="space-y-2">
              <Label htmlFor="fecha_inicio">Fecha Inicio</Label>
              <Input
                id="fecha_inicio"
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fecha_fin">Fecha Fin</Label>
              <Input
                id="fecha_fin"
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="zona_filtro">Zona (opcional)</Label>
              <select
                id="zona_filtro"
                value={zonaFiltro}
                onChange={(e) => setZonaFiltro(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
              >
                <option value="">Todas las zonas</option>
                {zonasDisponibles.map((zona) => (
                  <option key={zona} value={zona}>{zona}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={exportDetailedExcel}
                disabled={exporting}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {exporting ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                )}
                Descargar Excel
              </Button>
            </div>
          </div>

          {/* Presets de fechas */}
          <div className="flex flex-wrap gap-2">
            <span className="text-sm text-torta-muted py-1">Rangos rápidos:</span>
            <button
              onClick={() => setDatePreset('today')}
              className="px-3 py-1 text-sm bg-torta-cream rounded-full text-torta-brown hover:bg-torta-sand transition-colors"
            >
              Hoy
            </button>
            <button
              onClick={() => setDatePreset('yesterday')}
              className="px-3 py-1 text-sm bg-torta-cream rounded-full text-torta-brown hover:bg-torta-sand transition-colors"
            >
              Ayer
            </button>
            <button
              onClick={() => setDatePreset('week')}
              className="px-3 py-1 text-sm bg-torta-cream rounded-full text-torta-brown hover:bg-torta-sand transition-colors"
            >
              Última semana
            </button>
            <button
              onClick={() => setDatePreset('month')}
              className="px-3 py-1 text-sm bg-torta-cream rounded-full text-torta-brown hover:bg-torta-sand transition-colors"
            >
              Este mes
            </button>
            <button
              onClick={() => setDatePreset('lastMonth')}
              className="px-3 py-1 text-sm bg-torta-cream rounded-full text-torta-brown hover:bg-torta-sand transition-colors"
            >
              Mes anterior
            </button>
          </div>

          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Datos incluidos:</strong> ID de venta, fecha, hora, vendedor (nombre, zona, tipo, teléfono), 
              producto (nombre, cantidad, precio unitario, subtotal), método de pago, tipo de venta, 
              total de la venta, datos del cliente (nombre, teléfono, dirección) y notas.
            </p>
          </div>
        </div>
      )}

      {/* Alerta de Stock Bajo */}
      {productosStockBajo.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-red-800">Alerta de Inventario Bajo</h3>
              <p className="text-sm text-red-600 mb-3">
                Los siguientes productos tienen stock bajo en bodega:
              </p>
              <div className="flex flex-wrap gap-2">
                {productosStockBajo.map((p) => (
                  <span
                    key={p.id}
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                      (p.stock_central || 0) === 0
                        ? "bg-red-200 text-red-800"
                        : "bg-amber-100 text-amber-800"
                    }`}
                  >
                    {p.nombre}: {p.stock_central || 0} und
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-torta-brown to-[#A0522D] rounded-2xl p-6 text-white">
          <DollarSign className="w-8 h-8 mb-3 opacity-80" />
          <p className="text-white/80 text-sm">Total Ventas Hoy</p>
          <p className="font-display text-2xl">{formatCurrency(totalVentas)}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <Package className="w-8 h-8 text-blue-500 mb-3" />
          <p className="text-torta-muted text-sm">Unidades Vendidas</p>
          <p className="font-display text-2xl text-torta-dark">{totalUnidades}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <Users className="w-8 h-8 text-green-500 mb-3" />
          <p className="text-torta-muted text-sm">Vendedores Activos</p>
          <p className="font-display text-2xl text-torta-dark">{vendedores.length}</p>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <TrendingUp className="w-8 h-8 text-purple-500 mb-3" />
          <p className="text-torta-muted text-sm">Promedio de Meta</p>
          <p className="font-display text-2xl text-torta-dark">{promedioMeta.toFixed(1)}%</p>
        </div>
      </div>

      {/* Vendedores Table */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-torta-sand/50">
          <h2 className="font-display text-xl text-torta-dark">Rendimiento por Vendedor</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-torta-sand/30">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">#</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Vendedor</th>
                <th className="text-left p-4 text-sm font-medium text-torta-dark">Zona</th>
                <th className="text-right p-4 text-sm font-medium text-torta-dark">Ventas</th>
                <th className="text-right p-4 text-sm font-medium text-torta-dark">Unidades</th>
                <th className="text-right p-4 text-sm font-medium text-torta-dark">Meta</th>
                <th className="p-4 text-sm font-medium text-torta-dark">Cumplimiento</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-torta-sand/30">
              {vendedores.map((v, index) => (
                <tr key={v.id} className="hover:bg-torta-cream/30">
                  <td className="p-4">
                    <span
                      className={`inline-flex w-7 h-7 rounded-full items-center justify-center text-xs font-bold ${
                        index === 0
                          ? "bg-yellow-400 text-yellow-900"
                          : index === 1
                          ? "bg-gray-300 text-gray-700"
                          : index === 2
                          ? "bg-amber-500 text-amber-900"
                          : "bg-torta-sand text-torta-muted"
                      }`}
                    >
                      {index + 1}
                    </span>
                  </td>
                  <td className="p-4">
                    <p className="font-medium text-torta-dark">{v.nombre}</p>
                  </td>
                  <td className="p-4 text-torta-muted">{v.zona || "-"}</td>
                  <td className="p-4 text-right font-medium text-torta-dark">
                    {formatCurrency(v.ventas_hoy)}
                  </td>
                  <td className="p-4 text-right text-torta-dark">{v.unidades_hoy}</td>
                  <td className="p-4 text-right text-torta-muted">{formatCurrency(v.meta)}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            v.cumplimiento >= 100
                              ? "bg-green-500"
                              : v.cumplimiento >= 50
                              ? "bg-yellow-500"
                              : "bg-red-500"
                          }`}
                          style={{ width: `${Math.min(v.cumplimiento, 100)}%` }}
                        />
                      </div>
                      <span
                        className={`text-xs font-medium w-12 text-right ${
                          v.cumplimiento >= 100
                            ? "text-green-600"
                            : v.cumplimiento >= 50
                            ? "text-yellow-600"
                            : "text-red-600"
                        }`}
                      >
                        {v.cumplimiento}%
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {vendedores.length === 0 && (
          <p className="text-center py-8 text-torta-muted">No hay datos de vendedores</p>
        )}
      </div>

      {/* Stock por Producto */}
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6 border-b border-torta-sand/50">
          <h2 className="font-display text-xl text-torta-dark">Stock Central por Producto</h2>
        </div>
        <div className="p-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {productos.map((p) => (
              <div
                key={p.id}
                className={`p-4 rounded-xl border ${
                  (p.stock_central || 0) === 0
                    ? "border-red-200 bg-red-50"
                    : (p.stock_central || 0) < 10
                    ? "border-amber-200 bg-amber-50"
                    : "border-torta-sand bg-white"
                }`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-torta-dark">{p.nombre}</span>
                  <span
                    className={`px-2 py-1 rounded-full text-sm font-medium ${
                      (p.stock_central || 0) === 0
                        ? "bg-red-200 text-red-800"
                        : (p.stock_central || 0) < 10
                        ? "bg-amber-200 text-amber-800"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {p.stock_central || 0} und
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
